import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export default function HomePage() {
  const { data: leaderboardData, isLoading } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: api.getLeaderboard,
  });

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="text-center mb-16">
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          Which AI Sees Your Video Better?
        </h1>
        <p className="text-xl text-arena-muted max-w-2xl mx-auto mb-8">
          Compare scene detection AI models head-to-head in blind tests.
          Vote on which AI finds shot boundaries more accurately.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/battle" className="btn btn-primary text-lg px-8 py-4">
            Choose a Clip
          </Link>
          <Link to="/battle?quick=true" className="btn btn-secondary text-lg px-8 py-4">
            Quick Battle
          </Link>
          <Link to="/leaderboard" className="btn btn-secondary text-lg px-8 py-4">
            Leaderboard
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="card p-6 text-center">
            <div className="text-4xl mb-4">🎬</div>
            <h3 className="font-semibold mb-2">1. Watch a Clip</h3>
            <p className="text-arena-muted text-sm">
              See a short video clip with scene boundaries marked by two anonymous AI models
            </p>
          </div>
          <div className="card p-6 text-center">
            <div className="text-4xl mb-4">⚔️</div>
            <h3 className="font-semibold mb-2">2. Compare & Vote</h3>
            <p className="text-arena-muted text-sm">
              Decide which model detected shot boundaries more accurately
            </p>
          </div>
          <div className="card p-6 text-center">
            <div className="text-4xl mb-4">🏆</div>
            <h3 className="font-semibold mb-2">3. See Rankings</h3>
            <p className="text-arena-muted text-sm">
              Model identities revealed after voting. ELO ratings update in real-time
            </p>
          </div>
        </div>
      </section>

      {/* Leaderboard Preview */}
      <section className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Current Rankings</h2>
          <Link to="/leaderboard" className="text-arena-accent hover:underline text-sm">
            View full leaderboard →
          </Link>
        </div>

        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-arena-border text-arena-muted text-sm">
                <th className="text-left p-4 font-medium">Rank</th>
                <th className="text-left p-4 font-medium">Model</th>
                <th className="text-right p-4 font-medium">ELO</th>
                <th className="text-right p-4 font-medium hidden sm:table-cell">Win Rate</th>
                <th className="text-right p-4 font-medium hidden sm:table-cell">Battles</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-arena-muted">
                    Loading rankings...
                  </td>
                </tr>
              ) : leaderboardData?.leaderboard.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-arena-muted">
                    No battles yet. Be the first to vote!
                  </td>
                </tr>
              ) : (
                leaderboardData?.leaderboard.slice(0, 5).map((model, index) => (
                  <tr
                    key={model.id}
                    className="border-b border-arena-border last:border-0 hover:bg-arena-surface/50"
                  >
                    <td className="p-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                        index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                        index === 1 ? 'bg-gray-400/20 text-gray-400' :
                        index === 2 ? 'bg-orange-600/20 text-orange-600' :
                        'bg-arena-border text-arena-muted'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{model.display_name}</div>
                    </td>
                    <td className="p-4 text-right font-mono font-semibold">
                      {model.elo_rating}
                    </td>
                    <td className="p-4 text-right hidden sm:table-cell text-arena-muted">
                      {model.win_rate}%
                    </td>
                    <td className="p-4 text-right hidden sm:table-cell text-arena-muted">
                      {model.total_battles}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {leaderboardData && (
          <div className="text-center mt-4 text-arena-muted text-sm">
            {leaderboardData.total_votes.toLocaleString()} votes cast
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="text-center mt-16 py-12 card">
        <h2 className="text-2xl font-bold mb-4">Ready to be a Judge?</h2>
        <p className="text-arena-muted mb-6">
          Your votes help determine which AI best understands video editing cuts.
        </p>
        <Link to="/battle" className="btn btn-primary">
          Start Voting Now
        </Link>
      </section>
    </div>
  );
}
